import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconArrowRight, IconArrowUpRight, IconTemplate, IconTrash } from '@tabler/icons-react';
import styles from './ProjectList.module.scss';
import { inAppClick } from '../../utils/linkClick';
import type { ProjectListProject } from './ProjectList';
import ProjectTrendCell from './ProjectTrendCell';
import ScoreRing from '../ReportMetrics/widgets/ScoreRing';
import { KPI } from '../ReportMetrics/intelligence';
import { getProjectHealthStatus } from '../ReportMetrics/reportData';
import { hasComputedKpis } from '../ReportMetrics/portfolio';
import { chrome } from '../ReportMetrics/reportTheme';

interface ProjectCardViewProps {
  project: ProjectListProject;
  onDelete?: (projectId: string, event: React.MouseEvent) => void;
  onTemplateCreate?: (project: ProjectListProject, event: React.MouseEvent) => void;
  onClick?: () => void;
  /** Where the card leads; makes it a real link, so it can open in a new tab. */
  href?: string;
}

/** The four dimension scores under the health block, with their short labels. */
const CARD_DIMENSIONS = [
  { key: KPI.readinessScore, labelKey: 'ready', nameKey: 'readiness' },
  { key: KPI.executionScore, labelKey: 'exec', nameKey: 'execution' },
  { key: KPI.qualityScore, labelKey: 'qa', nameKey: 'quality' },
  { key: KPI.dependencyScore, labelKey: 'deps', nameKey: 'dependencies' },
] as const;

const METHOD_CLASS: Record<string, string> = {
  AGILE: styles.agileMethodBadge,
  CLASSIC: styles.classicMethodBadge,
  HYBRID: styles.hybridMethodBadge,
};

/**
 * A project on the Dashboard as a card: what kind of project it is, its health
 * with the weakest dimension named, the four dimension scores, progress, the
 * health trend and open actions - and the way into the project.
 */
const ProjectCardView: React.FC<ProjectCardViewProps> = ({ project, onDelete, onTemplateCreate, onClick, href }) => {
  const { t } = useTranslation(['project', 'templates']);
  const intelligence = hasComputedKpis(project.intelligence) ? project.intelligence : undefined;
  const health = intelligence?.health;
  const band = health != null ? getProjectHealthStatus(health) : undefined;

  const scored = CARD_DIMENSIONS.map((d) => ({ ...d, value: intelligence?.scores.get(d.key) })).filter(
    (d): d is typeof d & { value: number } => typeof d.value === 'number',
  );
  const weakest = scored.length > 1 ? scored.reduce((min, d) => (d.value < min.value ? d : min)) : undefined;

  const statusText =
    project.progress === 0
      ? t('list.status.planned')
      : project.progress === 100
        ? t('list.status.done')
        : t('list.status.inProgress');
  const statusClass =
    project.progress === 0 ? styles.statusPlaceholder : project.progress === 100 ? styles.statusCompleted : styles.statusInProgress;
  const methodText = project.method ? t(`list.method.${project.method.toLowerCase()}`) : undefined;

  const lastEdit = new Date(project.lastEdit.date);
  const lastEditText = Number.isNaN(lastEdit.getTime()) ? '—' : lastEdit.toLocaleDateString('de-CH');

  return (
    <div
      className={styles.projectCardView}
      onClick={href ? undefined : onClick}
      style={{ cursor: onClick || href ? 'pointer' : 'default' }}
    >
      {/* covers the whole card; the action buttons sit above it */}
      {href && (
        <a
          href={href}
          className={styles.pcStretchedLink}
          aria-label={project.title}
          onClick={inAppClick(() => onClick?.())}
        />
      )}
      <div className={styles.cardHeader}>
        <img src={project.logoUrl} alt={project.title} className={styles.cardLogo} />
        <div className={styles.cardInfo}>
          <h3 className={styles.cardTitle}>{project.title}</h3>
          <p className={styles.cardDescription}>{project.description}</p>
        </div>
        <div className={styles.cardActionGroup}>
          {onTemplateCreate && (
            <button
              className={styles.cardTemplateButton}
              onClick={(e) => onTemplateCreate(project, e)}
              title={t('list.actions.createTemplate')}
            >
              <IconTemplate size={16} />
            </button>
          )}
          {onDelete && (
            <button
              className={styles.cardDeleteButton}
              onClick={(e) => onDelete(project.id, e)}
              title={t('list.card.deleteTitle')}
            >
              <IconTrash size={16} />
            </button>
          )}
          <IconArrowUpRight size={18} className={styles.pcOpenIcon} />
        </div>
      </div>

      <div className={styles.pcChips}>
        {methodText && (
          <span className={`${styles.badge} ${METHOD_CLASS[project.method ?? ''] ?? styles.agileMethodBadge}`}>{methodText}</span>
        )}
        <span className={`${styles.badge} ${statusClass}`}>{statusText}</span>
        {project.teamMembers.slice(0, 2).map((member) => (
          <span key={member.id} className={styles.pcCharacter} title={member.name}>
            {member.name}
          </span>
        ))}
        {project.teamMembers.length > 2 && (
          <span className={styles.cardCharacterMore}>+{project.teamMembers.length - 2}</span>
        )}
      </div>

      {intelligence && (
        <div className={styles.pcSection}>
          <div className={styles.pcHealth}>
            <ScoreRing value={health} color={band ? band.color : chrome.muted} size={56} />
            <div className={styles.pcHealthText}>
              <span className={styles.pcHealthTitle}>{t('templates:report.health.title')}</span>
              {band && (
                <span className={styles.pcHealthBadge} style={{ color: band.color, background: `${band.color}1A` }}>
                  {t(`templates:report.health.status.${band.key}`)}
                </span>
              )}
              {weakest && (
                <span className={styles.pcHealthHint}>
                  {t('list.card.weakest', { dimension: t(`list.card.dimension.${weakest.nameKey}`) })}
                </span>
              )}
            </div>
          </div>
          <div className={styles.pcDimensions}>
            {CARD_DIMENSIONS.map((dimension) => {
              const value = intelligence.scores.get(dimension.key);
              return (
                <div key={dimension.key} title={t(`list.card.dimension.${dimension.nameKey}`)}>
                  <span className={styles.pcDimensionLabel}>{t(`list.card.short.${dimension.labelKey}`)}</span>
                  <span
                    className={styles.pcDimensionValue}
                    style={{ color: value != null ? getProjectHealthStatus(value).color : chrome.muted }}
                  >
                    {value != null ? `${Math.round(value)}%` : '—'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className={styles.pcSection}>
        <div className={styles.pcRow}>
          <span className={styles.pcLabel}>{t('list.card.progress')}</span>
          <span className={styles.pcValue}>{project.progress} %</span>
        </div>
        <div className={styles.cardProgressTrack}>
          <div
            className={`${styles.cardProgressFill} ${
              project.progress === 100 ? styles.progressFull : project.progress >= 50 ? styles.progressMedium : styles.progressLow
            }`}
            style={{ width: `${project.progress}%` }}
          />
        </div>
        {intelligence && (
          <div className={styles.pcRow} style={{ marginTop: 12 }}>
            <div className={styles.pcStack}>
              <span className={styles.pcLabel}>{t('list.card.healthTrend')}</span>
              <ProjectTrendCell value={intelligence.healthTrend} />
            </div>
            <div className={`${styles.pcStack} ${styles.pcStackEnd}`}>
              <span className={styles.pcLabel}>{t('list.card.openActions')}</span>
              <span className={styles.pcValue}>{intelligence.openAlerts}</span>
            </div>
          </div>
        )}
      </div>

      <div className={styles.pcFooter}>
        <span>{t('list.card.lastEditOn', { date: lastEditText })}</span>
        <span className={styles.pcOpen}>
          {t('list.card.open')} <IconArrowRight size={14} />
        </span>
      </div>
    </div>
  );
};

export default ProjectCardView;
